import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Confirmation } from 'core/components';
import { AgGridFilterOption } from 'core/enums';
import { useAgGridClientTableContext } from 'core/states';
import { PartDetails, PartDetailsTab } from 'parts/components/PartDetails';
import { IExternalPricesCustomization } from 'parts/types';
import { PricingHelper } from 'pricing/helpers';
import { isEditable } from 'quotes/components/CustomerQuotePartList/utils';
import { QuoteVersionType } from 'quotes/enums';
import { useQuotePartActions } from 'quotes/hooks';
import { useCustomerQuotePartsContext, useSelectedQuotePartContext } from 'quotes/states';
import { ICustomerQuote, ICustomerQuotePart, IQuotePart } from 'quotes/types';

interface IProps {
  quote: ICustomerQuote;
  quotePart?: Partial<IQuotePart>;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  defaultTab?: PartDetailsTab;
  onReusePrice?: (unitPrice: number) => void;
}

export const QuotePartDetails = ({
  quote,
  quotePart: initialQuotePart = {},
  isOpen,
  onClose,
  onSubmitSuccess,
  defaultTab: customDefaultTab,
  onReusePrice: customOnReusePrice,
}: IProps) => {
  const [quotePart, setQuotePart] = useState<Partial<IQuotePart>>(initialQuotePart);
  const [reusePrice, setReusePrice] = useState<number | null>(null);

  const { t } = useTranslation();

  const { selectedQuotePart } = useSelectedQuotePartContext();
  const { currentQuoteVersion } = useCustomerQuotePartsContext();
  const { updateRows } = useAgGridClientTableContext();
  const { upsertQuoteParts } = useQuotePartActions();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!initialQuotePart?.partArr?.number) {
      setQuotePart(selectedQuotePart);

      return;
    }

    setQuotePart(initialQuotePart);
  }, [initialQuotePart, selectedQuotePart, isOpen]);

  const isReusePriceDisabled = !isEditable({
    data: quotePart as ICustomerQuotePart,
    currentQuoteVersion,
  });

  const reusePriceDisabledTooltip =
    QuoteVersionType.RELEASE === currentQuoteVersion ? t('messageReusePriceDisabled') : undefined;

  const onReusePrice = useCallback(
    (unitPrice: number) => {
      if (customOnReusePrice) {
        customOnReusePrice(unitPrice);

        return;
      }

      setReusePrice(unitPrice);
    },
    [customOnReusePrice]
  );

  const onConfirmReusePrice = useCallback(async () => {
    if (reusePrice === null || !quotePart?.id) {
      return;
    }

    const payload = {
      quoteParts: [{ id: quotePart.id, price: reusePrice, isPricingCustom: 1 }],
    };

    setReusePrice(null);

    const { success, quoteParts } = await upsertQuoteParts(payload, {
      onSuccess: onSubmitSuccess,
    });

    if (!success) {
      return;
    }

    updateRows({ update: quoteParts }, { refreshCells: true, redrawRows: true });
  }, [reusePrice, quotePart?.id, upsertQuoteParts, onSubmitSuccess, updateRows]);

  const onCancelReusePrice = useCallback(() => {
    setReusePrice(null);
  }, []);

  const partDetailsPayload = useMemo(
    () => ({
      accountsReceivableId: quote.billToAddress?.accountsReceivableId,
      salesExternalLocationId: quote.location?.externalLocationId,
    }),
    [quote.billToAddress?.accountsReceivableId, quote.location?.externalLocationId]
  );

  const historyConfig = useMemo(
    () => ({
      requestPayloadFilters: { excludeQuoteId: quote.id },
      initialFilter: {
        customerContactGroupName: {
          type: AgGridFilterOption.CONTAINS,
          filter: quote.customerContactGroup.name,
        },
      },
    }),
    [quote.id, quote.customerContactGroup.name]
  );

  const salesHistoryConfig = useMemo(
    () => ({
      partNumber: quotePart?.alternateVendorPartNumber,
      initialFilter: {
        ...(quote.location?.externalLocationId && {
          salesLocation: {
            type: AgGridFilterOption.CONTAINS,
            filter: quote.location.externalLocationId,
          },
        }),
        ...(quote.billToAddress?.accountsReceivableId && {
          billToAccount: {
            type: AgGridFilterOption.CONTAINS,
            filter: quote.billToAddress.accountsReceivableId,
          },
        }),
      },
    }),
    [
      quotePart?.alternateVendorPartNumber,
      quote.location?.externalLocationId,
      quote.billToAddress?.accountsReceivableId,
    ]
  );

  const subtitleAdditions = useMemo(
    () => ({ updatedAt: quotePart?.updatedAt }),
    [quotePart?.updatedAt]
  );

  const customerQuotePart = quotePart as Partial<ICustomerQuotePart>;

  const customizationDefaults = useMemo<Partial<IExternalPricesCustomization>>(
    () => ({
      shippingLocation: customerQuotePart?.corporateLocation,
      salesLocation: quote.location,
      shipmentType: customerQuotePart?.shipmentType,
      customerBillTo: quote.billToAddress,
      project: quote.vendorProjectId
        ? { id: quote.vendorProjectId, name: quote.vendorProjectName }
        : null,
    }),
    [
      customerQuotePart?.corporateLocation,
      quote.location,
      customerQuotePart?.shipmentType,
      quote.billToAddress,
      quote.vendorProjectId,
      quote.vendorProjectName,
    ]
  );

  const slidingPanelProps = useMemo(() => ({ autoWidth: true, interactive: true }), []);

  return (
    <>
      <PartDetails
        part={quotePart?.partArr}
        isOpen={isOpen}
        onClose={onClose}
        defaultTab={customDefaultTab}
        onReusePrice={onReusePrice}
        isAlternateVersionEnabled={Boolean(quote.alternateVersionEnabled)}
        partDetailsPayload={partDetailsPayload}
        reusePriceDisabled={isReusePriceDisabled}
        reusePriceDisabledTooltip={reusePriceDisabledTooltip}
        slidingPanelProps={slidingPanelProps}
        historyConfig={historyConfig}
        salesHistoryConfig={salesHistoryConfig}
        subtitleAdditions={subtitleAdditions}
        customizationDefaults={customizationDefaults}
      />

      <Confirmation
        isOpen={reusePrice !== null}
        title={t('titleReusePriceConfirmation')}
        message={t('messageReusePriceConfirmation', {
          price: reusePrice !== null ? PricingHelper.formatPricing({ amount: reusePrice }) : '',
        })}
        confirmBtnLabel={t('buttonLabelApply')}
        onConfirm={onConfirmReusePrice}
        onCancel={onCancelReusePrice}
      />
    </>
  );
};
